import type { Distance, Margins, Point, SpatialRectangle } from "@pdfking/math"
import { Abs, RemoveMargins, RotateMarginsLeft, Round } from "@pdfking/math"
import { QUARTER_TURN, ZERO_DEGREE, ZERO_DISTANCE } from "@pdfking/math/constants"
import { NullQ } from "@pdfking/structs"
import { DeleteDuplicates, SortBy } from "@pdfking/tfunc"

import type { CrossmarkGrid, ResolvedLayout, ResolvedPage } from "#public"

import type { FillNodeData } from "../types/node"
import { get_used_symmetry, plot_origin, plot_total_size } from "./Geometry"
import { internal_coords } from "./InternalCoords"
import { internal_matrix } from "./InternalMatrix"

const UPRIGHT = ZERO_DEGREE
const TURNED = QUARTER_TURN

/**
 * Mirrors Python `_ensure_margin(job, m)` (strategy.py:1243-1254): an open
 * edge may bleed as far as the fill's own edge and no further; a closed one
 * not at all. No cap of its own — the plot caps it with the real bleed.
 *
 * Python's y grows down and this one grows up, and the formula is carried over
 * as it stands: `top` is clamped against the near edge, `bottom` against the
 * far one. The names swap with the axis; the paper does not.
 */
const ensure_margin = (
  page: SpatialRectangle<Distance>,
  open: Margins<boolean>,
  frame: SpatialRectangle<Distance>
): Margins<Distance> =>
  ({
    top: open.top ? Abs(frame.y - page.y) : ZERO_DISTANCE,
    right: open.right ? Abs(frame.x + frame.width - (page.x + page.width)) : ZERO_DISTANCE,
    bottom: open.bottom ? Abs(frame.y + frame.height - (page.y + page.height)) : ZERO_DISTANCE,
    left: open.left ? Abs(frame.x - page.x) : ZERO_DISTANCE,
  }) as Margins<Distance>

/**
 * Every cut along one axis, an edge two pages share counted once. Distances
 * are floats; a cut is one line, so the edges are read to the micron.
 */
const cuts = (size: Distance, stride: Distance, count: number): Distance[] =>
  SortBy(
    DeleteDuplicates(
      Array.from({ length: count }, (_, i) => [
        (stride * i) as Distance,
        (stride * i + size) as Distance,
      ]).flat(),
      Round
    ),
    Round
  )

/**
 * The cuts of every block, each in its own frame: `position` is the outline of
 * the block's pages, and the cuts are measured from its origin.
 *
 * The outline is the pages themselves, not `stride * matrix`, which would
 * count the padding outside the outermost pages and stand the cuts off the
 * artwork by it.
 */
const resolve_crossmarks = (
  data: FillNodeData,
  frame: SpatialRectangle<Distance>
): CrossmarkGrid[] =>
  internal_matrix(data, frame).map(({ open, matrix, offset, rotated }) => {
    const size: Point<Distance> = {
      x: (rotated ? data.inner.height : data.inner.width) as Distance,
      y: (rotated ? data.inner.width : data.inner.height) as Distance,
    }
    const padding = rotated ? RotateMarginsLeft(data.padding) : data.padding
    const stride: Point<Distance> = {
      x: (size.x + padding.left + padding.right) as Distance,
      y: (size.y + padding.top + padding.bottom) as Distance,
    }
    const cuts_x = cuts(size.x, stride.x, matrix.x)
    const cuts_y = cuts(size.y, stride.y, matrix.y)
    const position: SpatialRectangle<Distance> = {
      height: (stride.y * (matrix.y - 1) + size.y) as Distance,
      width: (stride.x * (matrix.x - 1) + size.x) as Distance,
      x: (offset.x + padding.left) as Distance,
      y: (offset.y + padding.bottom) as Distance,
    }
    return { bleed: ensure_margin(position, open, frame), cuts_x, cuts_y, position }
  })

/**
 * Everything that follows from a fill: where each page lands, where the cuts
 * fall, what the whole thing covers.
 *
 * Coordinates are the fill's own frame — the search may have turned the sheet,
 * and it answers in the frame it filled. A caller that plots the fill
 * somewhere else moves the whole answer with it.
 *
 * Under symmetry only the half that is printed comes back: the other half is
 * that one mirrored about `mirror.center`, which is a job for whoever owns the
 * pages, since a mirrored page is not just a moved one.
 */
export const resolve_fill = (data: FillNodeData & SpatialRectangle<Distance>): ResolvedLayout => {
  const frame = data
  const origin = plot_origin(data, frame)
  const total = plot_total_size(data)
  const axis = data.use_symmetry ? get_used_symmetry(data) : null
  const center = axis ? ((origin[axis] + total[axis] / 2) as Distance) : undefined

  const pages: ResolvedPage[] = []
  for (const { open, coord, rotated } of internal_coords(data, frame)) {
    if (axis && !NullQ(center) && coord[axis] >= center) continue
    const position: SpatialRectangle<Distance> = {
      height: (rotated ? data.inner.width : data.inner.height) as Distance,
      width: (rotated ? data.inner.height : data.inner.width) as Distance,
      x: coord.x,
      y: coord.y,
    }
    pages.push({
      bleed: ensure_margin(position, open, frame),
      guid: [],
      padding: data.padding,
      position,
      rotation: rotated ? TURNED : UPRIGHT,
    })
  }

  return {
    crossmarks: resolve_crossmarks(data, frame),
    mirror: axis && !NullQ(center) ? { axis, center } : undefined,
    pages,
    position: {
      height: total.y,
      width: total.x,
      x: origin.x,
      y: origin.y,
    },
    security: RemoveMargins(frame, data.security),
  }
}
